import { memo, useEffect, useState } from 'react'
import { programsApi, type CatalogFacets } from '../../lib/api.js'
import './ProgramsView.css'

/**
 * Colonne « Affiner » du catalogue.
 *
 * Chaque facette porte son compte : l'opérateur sait ce qu'il obtiendra avant
 * de cliquer, et une facette à zéro reste visible plutôt que de disparaître —
 * « aucune fiche sans genre » est une information, pas une absence.
 *
 * Les compteurs viennent du serveur et ignorent les facettes déjà cochées,
 * sans quoi la facette active afficherait son propre total et les autres
 * tomberaient à zéro dès le premier clic.
 */

export interface CatalogFacetSelection {
  readiness?: 'incomplete' | 'genreless' | 'incomplete-today'
  mediaState?: 'available' | 'none' | 'missing-file'
  programGenreId?: string
  /** `true` : uniquement les programmes en direct. */
  isLive?: boolean
  /**
   * Périmètre d'archivage. `undefined` vaut 'active' : le catalogue montre les
   * fiches actives par défaut, l'archive ne s'ouvre qu'à la demande.
   */
  archived?: 'active' | 'archived' | 'all'
}

interface CatalogFacetsPanelProps {
  channelId: string
  /** Filtres structurants en cours (recherche, série, collection…). */
  baseQuery: Record<string, unknown>
  selection: CatalogFacetSelection
  onChange: (selection: CatalogFacetSelection) => void
  /** Change à chaque écriture du catalogue, pour recompter. */
  refreshKey?: number
  /** Partage les mêmes chiffres avec la bande de synthèse Pen. */
  onLoaded?: (facets: CatalogFacets | null) => void
  /** Repli calculé sur les fiches déjà chargées si le comptage distant échoue. */
  fallbackFacets: CatalogFacets
}

export const CatalogFacetsPanel = memo(function CatalogFacetsPanel({
  channelId, baseQuery, selection, onChange, refreshKey = 0, onLoaded, fallbackFacets,
}: CatalogFacetsPanelProps) {
  const [facets, setFacets] = useState<CatalogFacets | null>(null)

  const queryKey = JSON.stringify(baseQuery)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const result = await programsApi.catalogFacets({ ...JSON.parse(queryKey), channelId })
        if (!cancelled) {
          setFacets(result)
          onLoaded?.(result)
        }
      } catch {
        // Une facette indisponible ne doit pas vider l'écran : la liste reste
        // utilisable sans sa colonne de filtres.
        if (!cancelled) {
          setFacets(null)
          onLoaded?.(null)
        }
      }
    })()
    return () => { cancelled = true }
  }, [channelId, queryKey, refreshKey, onLoaded])

  const hasSelection = Boolean(
    selection.readiness || selection.mediaState || selection.programGenreId || selection.isLive ||
    (selection.archived && selection.archived !== 'active'),
  )
  const resolvedFacets = facets ?? fallbackFacets

  /** Un second clic sur une facette active la retire. */
  const toggle = <K extends keyof CatalogFacetSelection>(key: K, value: CatalogFacetSelection[K]) => {
    onChange({ ...selection, [key]: selection[key] === value ? undefined : value })
  }

  return (
    <aside className="catalog-facets" aria-label="Affiner le catalogue">
      <div className="catalog-facets__header">
        <span className="catalog-facets__title">Affiner</span>
        <button
          type="button"
          className="catalog-facets__reset"
          onClick={() => onChange({})}
          disabled={!hasSelection}
        >
          Réinitialiser
        </button>
      </div>

      <FacetGroup label="État">
        <FacetRow
          label="Tous les programmes"
          count={resolvedFacets.state.all}
          active={!selection.readiness}
          onClick={() => onChange({ ...selection, readiness: undefined })}
        />
        <FacetRow
          label="À compléter"
          count={resolvedFacets.state.incomplete}
          active={selection.readiness === 'incomplete'}
          onClick={() => toggle('readiness', 'incomplete')}
        />
        {/* Ce qui passe ce soir et n'est pas prêt : la seule liste de la
            colonne qui appelle une action avant la fin de journée. */}
        <FacetRow
          label="À compléter — aujourd’hui"
          count={resolvedFacets.state.incompleteToday}
          active={selection.readiness === 'incomplete-today'}
          onClick={() => toggle('readiness', 'incomplete-today')}
        />
        <FacetRow
          label="Sans genre"
          count={resolvedFacets.state.genreless}
          active={selection.readiness === 'genreless'}
          onClick={() => toggle('readiness', 'genreless')}
        />
        <FacetRow
          label="Directs"
          count={resolvedFacets.live}
          active={selection.isLive === true}
          onClick={() => onChange({ ...selection, isLive: selection.isLive ? undefined : true })}
        />
      </FacetGroup>

      {/* Périmètre, pas état : l'archive est une autre étagère du catalogue, et
          les trois choix sont exclusifs — un second clic ne les retire pas, il
          faut toujours regarder quelque part. */}
      <FacetGroup label="Archivage">
        <FacetRow
          label="Actifs"
          count={resolvedFacets.archive.active}
          active={!selection.archived || selection.archived === 'active'}
          onClick={() => onChange({ ...selection, archived: undefined })}
        />
        <FacetRow
          label="Archivés"
          count={resolvedFacets.archive.archived}
          active={selection.archived === 'archived'}
          onClick={() => onChange({ ...selection, archived: 'archived' })}
        />
        <FacetRow
          label="Actifs et archivés"
          count={resolvedFacets.archive.all}
          active={selection.archived === 'all'}
          onClick={() => onChange({ ...selection, archived: 'all' })}
        />
      </FacetGroup>

      <FacetGroup label="Média">
        <FacetRow
          label="Fichier disponible"
          count={resolvedFacets.media.available}
          active={selection.mediaState === 'available'}
          onClick={() => toggle('mediaState', 'available')}
        />
        <FacetRow
          label="Sans média"
          count={resolvedFacets.media.none}
          active={selection.mediaState === 'none'}
          onClick={() => toggle('mediaState', 'none')}
        />
        <FacetRow
          label="Fichier absent du disque"
          count={resolvedFacets.state.missingFile}
          active={selection.mediaState === 'missing-file'}
          onClick={() => toggle('mediaState', 'missing-file')}
        />
      </FacetGroup>

      {resolvedFacets.genres.length > 0 && (
        <FacetGroup label="Genre">
          {resolvedFacets.genres.map(genre => (
            <FacetRow
              key={genre.id}
              label={genre.label}
              count={genre.count}
              color={genre.color}
              active={selection.programGenreId === genre.id}
              onClick={() => toggle('programGenreId', genre.id)}
            />
          ))}
        </FacetGroup>
      )}
    </aside>
  )
})

const FacetGroup = memo(function FacetGroup({ label, children }: {
  label: string; children: React.ReactNode
}) {
  return (
    <div className="catalog-facets__group">
      <div className="catalog-facets__group-label">{label}</div>
      {children}
    </div>
  )
})

const FacetRow = memo(function FacetRow({ label, count, active, color, onClick }: {
  label:   string
  /** `undefined` tant que les compteurs ne sont pas chargés. */
  count?:  number
  active:  boolean
  color?:  string | null
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className={`catalog-facets__row${active ? ' is-active' : ''}`}
      aria-pressed={active}
      onClick={onClick}
    >
      <span className="catalog-facets__dot" style={color ? { background: color } : undefined} aria-hidden="true" />
      <span className="catalog-facets__label">{label}</span>
      <span className="catalog-facets__count">{count ?? '—'}</span>
    </button>
  )
})
